import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { setSelectedAppointment } from "../../store/slices/appointmentsSlice";
import AppointmentSummary from "./appointments/AppointmentSummary";
import { 
  CalendarIcon, 
  PhoneIcon, 
  EnvelopeIcon, 
  CreditCardIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ClockIcon,
  DocumentCheckIcon,
  XMarkIcon,
  CreditCardIcon as CreditCardIconSolid
} from "@heroicons/react/24/outline";

export default function FrontDeskTodoItem({ todo, onReschedule, onWideSidePanelClose }) {
  const dispatch = useDispatch();
  const [isPanelOpen, setIsPanelOpen] = useState(false);

  const handleActionClick = () => {
    dispatch(setSelectedAppointment(todo));
    setIsPanelOpen(true);
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    const options = {
      weekday: "short",
      month: "short",
      day: "numeric",
    };
    const timeOptions = { hour: "numeric", minute: "numeric", hour12: true };
    return `${date.toLocaleDateString("en-US", options)}, ${date.toLocaleTimeString("en-US", timeOptions)}`;
  };

  const handleClosePanel = () => {
    setIsPanelOpen(false);
    if (onWideSidePanelClose) {
      onWideSidePanelClose();
    }
  };

  const getTimeIndicator = (dateString) => {
    const appointmentDate = new Date(dateString);
    const now = new Date();
    const diffHours = (appointmentDate - now) / (1000 * 60 * 60);
    const diffDays = Math.abs(Math.floor(diffHours / 24));
    
    if (diffHours < 0) {
      return <span className="text-red-600 font-medium">{diffDays} {diffDays === 1 ? 'day' : 'days'} past due</span>;
    } else if (diffHours < 24) {
      return <span className="text-red-500 font-medium">Today</span>;
    } else if (diffHours < 48) {
      return <span className="text-amber-500 font-medium">Tomorrow</span>;
    } else {
      return <span className="text-blue-500">{diffDays} {diffDays === 1 ? 'day' : 'days'} away</span>;
    }
  };

  const getTodoIcon = () => {
    if (todo.type === "Confirm") {
      return <PhoneIcon className="h-5 w-5 text-indigo-500" />;
    } else if (todo.type === "Invoice") {
      return <DocumentTextIcon className="h-5 w-5 text-green-500" />;
    } else if (todo.type === "Payment") {
      return <CreditCardIcon className="h-5 w-5 text-amber-500" />;
    } else {
      return <CheckCircleIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getTodoActionText = () => {
    if (todo.type === "Confirm") {
      return "Confirm Appointment";
    } else if (todo.type === "Invoice") {
      return "Create Invoice";
    } else if (todo.type === "Payment") {
      return "Collect Payment";
    } else {
      return "Take Action";
    }
  };

  const getTodoActionButtonClass = () => {
    if (todo.type === "Confirm") {
      return "text-indigo-700 bg-indigo-100 hover:bg-indigo-200";
    } else if (todo.type === "Invoice") {
      return "text-green-700 bg-green-100 hover:bg-green-200";
    } else if (todo.type === "Payment") {
      return "text-amber-700 bg-amber-100 hover:bg-amber-200";
    } else {
      return "text-gray-700 bg-gray-100 hover:bg-gray-200";
    }
  };

  const getBadgeStyles = (type) => {
    switch (type) {
      case 'Confirm':
        return 'bg-indigo-50 text-indigo-800 ring-indigo-600/20';
      case 'Invoice':
        return 'bg-green-50 text-green-800 ring-green-600/20';
      case 'Payment':
        return 'bg-amber-50 text-amber-800 ring-amber-600/20';
      default:
        return 'bg-gray-50 text-gray-800 ring-gray-600/20';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  return (
    <>
      <div 
        className={`rounded-sm shadow-sm border p-4 mb-3 hover:shadow-md transition-shadow duration-200 cursor-pointer ${
          todo.type === "Confirm" ? "bg-indigo-50 border-indigo-100" :
          todo.type === "Invoice" ? "bg-green-50 border-green-100" :
          todo.type === "Payment" ? "bg-amber-50 border-amber-100" :
          "bg-white border-gray-200"
        }`}
        onClick={handleActionClick}
      >
        <div className="flex items-start">
          <div className="flex-shrink-0 mr-3 mt-1">
            {getTodoIcon()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <h3 className={`text-sm font-medium truncate ${
                  todo.type === "Confirm" ? "text-indigo-900" :
                  todo.type === "Invoice" ? "text-green-900" :
                  todo.type === "Payment" ? "text-amber-900" :
                  "text-gray-900"
                }`}>
                  {todo.client?.firstName} {todo.client?.lastName}
                </h3>
                {todo.clinic?.name && (
                  <p className="text-xs text-gray-500 truncate">
                    {todo.clinic.name}
                  </p>
                )}
              </div>
              <div className="ml-2 flex-shrink-0">
                {getTimeIndicator(todo.start)}
              </div>
            </div>
            
            <div className="mt-1 flex items-center justify-between">
              <div className="flex items-center text-sm text-gray-500">
                <CalendarIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                <p>{formatDateTime(todo.start)}</p>
              </div>
              <div className="flex items-center gap-2">
                {todo.client?.contract?.signed ? (
                  <DocumentCheckIcon className="h-4 w-4 text-green-500" title="Contract Signed" />
                ) : (
                  <DocumentTextIcon className="h-4 w-4 text-red-500" title="Contract Not Signed" />
                )}
                {todo.client?.creditCardOnFile ? (
                  <CreditCardIconSolid className="h-4 w-4 text-green-500" title="Credit Card on File" />
                ) : (
                  <CreditCardIcon className="h-4 w-4 text-red-500" title="No Credit Card on File" />
                )}
              </div>
            </div>
            
            <div className="mt-1 text-sm text-gray-500 flex items-center justify-between">
              <p>{todo.service?.description}</p>
              <span className="text-xs text-gray-500">
                {todo.client?.defaultPaymentMethod && todo.client.defaultPaymentMethod.name ? (
                  <>
                    Default: {todo.client.defaultPaymentMethod.name}
                    {todo.client.defaultPaymentMethod.lastFourDigits && 
                      ` (${todo.client.defaultPaymentMethod.lastFourDigits})`}
                  </>
                ) : (
                  "Not assigned"
                )}
              </span>
            </div>

            <div className="mt-3 flex items-center justify-between">
              <div className="flex items-center">
                {todo.type === "Confirm" && (
                  <div className="flex items-center text-xs text-indigo-600">
                    <PhoneIcon className="h-4 w-4 mr-1" />
                    <span>Call client to confirm</span>
                  </div>
                )}
                {todo.type === "Invoice" && (
                  <div className="flex items-center text-xs text-green-600">
                    <DocumentTextIcon className="h-4 w-4 mr-1" />
                    <span>Create invoice for treatment</span>
                  </div>
                )}
                {todo.type === "Payment" && (
                  <div className="flex items-center text-xs text-amber-600">
                    <CreditCardIcon className="h-4 w-4 mr-1" />
                    <span>Collect payment for invoice</span>
                  </div>
                )}
              </div>
              
              <button
                className={`inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${getTodoActionButtonClass()}`}
              >
                {getTodoActionText()}
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {isPanelOpen && (
        <AppointmentSummary
          title="Appointment Details"
          open={isPanelOpen}
          onClose={handleClosePanel}
          onReschedule={onReschedule}
        />
      )}
    </>
  );
} 